// 118 — Solicitud de Sangre (Fase 7, Tanda 5, ver PLAN.md). Transcripción campo por campo del PDF oficial del
// Anexo 1 MSP: hoja idx 247 (footer `SNS-MSP/HCU-form.118/2021`, 1 hoja). Informe de brechas en
// `docs/hce-fase7/brechas-117-118-119-120-122.md`.
//
// Retirado de la UI por no estar impreso en el papel: "Tipo de sangre" (el grupo ABO/Rh aparece solo como dato
// de laboratorio, bloque D), "Unidades solicitadas" (las reemplaza la tabla de componentes) y "Motivo de la
// solicitud". Los recursos ya guardados no se tocan.
//
// Decisiones ante vacíos del papel: "Alistar" = casilla + fecha + hora literales, sin lógica; la sigla del
// crioprecipitado está recortada en el escaneo (CRIC/CRIO) y se deja solo el nombre completo; los laboratorios
// no traen unidades impresas (texto libre); la firma de quien toma la muestra es manuscrita (se captura nombre,
// fecha y hora); el texto legal de F va fijo — la firma digital del médico equivale a la aceptación.
//
// FHIR (consumidores: `SelectorRecurso` de 122, que muestra `code.text`): sigue siendo un `ServiceRequest`
// `active`/`order`, categoría "Banco de sangre", `priority` (Emergencia→stat, Urgencia→urgent, Rutina→routine),
// `code.text` legible con los componentes pedidos y `reasonCode` = diagnóstico para transfusión y principal.
import {
  conCodigoCie,
  OPCIONES_SI_NO,
  paresDeCampos,
  valoresVacios,
  type CampoSimple,
  type OpcionCampo,
  type Valores,
} from './camposSimplesDatos';
import type { DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import {
  estadoInicialTablaConColumnas,
  resumenFilaTablaConColumnas,
  type ColumnaTablaConColumnas,
  type EstadoTablaConColumnas,
  type FilaTablaConColumnas,
} from './uci/TablaConColumnas';
import { numeroALetras } from '../recetas/numeroALetras';
import type { Par } from './index';

const casilla = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'casilla' });
const opciones = (...etiquetas: readonly string[]): readonly OpcionCampo[] => etiquetas.map((e) => ({ valor: e, etiqueta: e }));

const OPCIONES_SI_NO_NO_SABE: readonly OpcionCampo[] = [...OPCIONES_SI_NO, { valor: 'nosabe', etiqueta: 'No sabe' }];

/** Prioridad de la solicitud: casillas del papel con su tiempo máximo y su `ServiceRequest.priority`. */
export const PRIORIDADES_118 = [
  { valor: 'Emergencia (5 min)', fhir: 'stat' },
  { valor: 'Urgencia (15 min)', fhir: 'urgent' },
  { valor: 'Rutina (45 min)', fhir: 'routine' },
] as const;

/** Sin prioridad elegida devuelve `undefined`: el papel obliga a marcarla, no hay valor por defecto (antes caía en "routine" en silencio). */
export function prioridadFhir118(prioridad: string | undefined): 'stat' | 'urgent' | 'routine' | undefined {
  return PRIORIDADES_118.find((p) => p.valor === prioridad)?.fhir;
}

// ---------- B. Servicio y prioridad de atención ----------

export const CAMPOS_SERVICIO_118: readonly CampoSimple[] = [
  { clave: 'servicio', etiqueta: 'Servicio', tipo: 'select', opciones: opciones('Emergencia', 'Consulta externa', 'Hospitalización') },
  { clave: 'especialidad', etiqueta: 'Especialidad' },
  { clave: 'sala', etiqueta: 'Sala' },
  { clave: 'cama', etiqueta: 'Cama' },
  { clave: 'categorizacion', etiqueta: 'Categorización del paciente', tipo: 'select', opciones: opciones('Clínico', 'Neonatológico', 'Quirúrgico', 'Gineco-obstétrico') },
  { clave: 'prioridad', etiqueta: 'Prioridad de la solicitud', tipo: 'select', opciones: opciones(...PRIORIDADES_118.map((p) => p.valor)) },
  casilla('alistar', 'Alistar'),
  { clave: 'alistarFecha', etiqueta: 'Alistar — fecha', tipo: 'fecha' },
  { clave: 'alistarHora', etiqueta: 'Alistar — hora', tipo: 'hora' },
  { clave: 'tratamiento', etiqueta: 'Tratamiento terapéutico (especifique nombre y tiempo de administración)' },
];

// ---------- C. Antecedentes transfusionales ----------

export const CAMPOS_ANTECEDENTES_118: readonly CampoSimple[] = [
  { clave: 'transfusionesAnteriores', etiqueta: 'Transfusiones anteriores', tipo: 'select', opciones: OPCIONES_SI_NO_NO_SABE },
  { clave: 'tiempoTransfusion', etiqueta: 'Tiempo de la transfusión anterior', tipo: 'select', opciones: opciones('< de 2 meses', '3-5 meses', '6-8 meses', '9-12 meses', '> 12 meses') },
  { clave: 'reaccionesAdversas', etiqueta: 'Reacciones adversas transfusionales previas', tipo: 'select', opciones: OPCIONES_SI_NO_NO_SABE },
  { clave: 'reaccionesEspecifique', etiqueta: 'Especifique (reacciones adversas)' },
];

// ---------- D. Datos actuales de laboratorio (sin unidades impresas) ----------

export const CAMPOS_LABORATORIO_118: readonly CampoSimple[] = [
  { clave: 'hemoglobina', etiqueta: 'Hemoglobina' },
  { clave: 'hematocrito', etiqueta: 'Hematocrito' },
  { clave: 'plaquetas', etiqueta: 'Plaquetas' },
  { clave: 'tp', etiqueta: 'TP' },
  { clave: 'ttp', etiqueta: 'TTP' },
  { clave: 'grupoAboRh', etiqueta: 'Grupo ABO/Rh' },
  { clave: 'inr', etiqueta: 'INR' },
  { clave: 'otros', etiqueta: 'Otros' },
];

// ---------- E. Tipo y número de componentes sanguíneos solicitados ----------

export const FILAS_COMPONENTES_118: readonly FilaTablaConColumnas[] = [
  { clave: 'cgre', etiqueta: 'Concentrado de glóbulos rojos estándar (CGRE)' },
  { clave: 'cgrsclp', etiqueta: 'Concentrado de glóbulos rojos sin capa leucoplaquetaria (CGRSCLp)' },
  { clave: 'cgrl', etiqueta: 'Concentrado de glóbulos rojos leucorreducidos (CGRL)' },
  { clave: 'cgrp', etiqueta: 'Concentrado de glóbulos rojos pediátricos (CGRP)' },
  { clave: 'cgrpsclp', etiqueta: 'Concentrado de glóbulos rojos pediátricos sin capa leucoplaquetaria (CGRPSCLp)' },
  { clave: 'cgrpl', etiqueta: 'Concentrado de glóbulos rojos pediátricos leucorreducidos (CGRPL)' },
  { clave: 'cgri', etiqueta: 'Concentrado de glóbulos rojos irradiados (CGRI)' },
  { clave: 'cp', etiqueta: 'Concentrado de plaquetas (CP)' },
  { clave: 'cpa', etiqueta: 'Concentrado de plaquetas por aféresis (CPA)' },
  { clave: 'pfc', etiqueta: 'Plasma fresco congelado (PFC)' },
  { clave: 'crio', etiqueta: 'Crioprecipitado' },
  { clave: 'pr', etiqueta: 'Plasma refrigerado (PR)' },
  { clave: 'sr', etiqueta: 'Sangre reconstituida (SR)' },
  { clave: 'otro', etiqueta: 'Otro' },
];

/** Cantidad entera positiva escrita en "Número"; `undefined` si la celda está vacía o no es un entero > 0 (hasta 999.999, límite de `numeroALetras`). */
export function cantidadComponente118(numero: string | undefined): number | undefined {
  const t = (numero ?? '').trim();
  if (!/^\d{1,6}$/.test(t)) return undefined;
  const n = Number(t);
  return n > 0 ? n : undefined;
}

/**
 * El papel pide el componente "en número y letras" (instructivo, bloque E): las letras se generan solas desde el número
 * (columna de solo lectura), así nunca pueden discrepar de él.
 */
export const COLUMNAS_COMPONENTES_118: readonly ColumnaTablaConColumnas[] = [
  { clave: 'numero', etiqueta: 'Número' },
  {
    clave: 'letras',
    etiqueta: 'Letras',
    calculada: (fila) => {
      const n = cantidadComponente118(fila.numero);
      return n === undefined ? '' : numeroALetras(n);
    },
  },
];

/** "Otro" no trae sigla en el papel: se describe aparte. */
export const CAMPOS_OTRO_COMPONENTE_118: readonly CampoSimple[] = [{ clave: 'otroComponente', etiqueta: 'Otro componente (especifique)' }];

/** Texto legal impreso del bloque F (referencia en pantalla; el "Yo, ___" es el médico prescriptor que firma). */
export const TEXTO_LEGAL_118 =
  'Yo, en calidad de médico prescriptor de este paciente, quien se encuentra en estado crítico, bajo mi responsabilidad solicito de manera urgente/emergente el (los) componente(s) sanguíneo(s) antes descrito(s), acepto que este componente sea enviado únicamente con la determinación de grupo ABO/RH con el fin de mejorar la condición de salud del paciente antes mencionado. Además solicito se completen las pruebas de compatibilidad sanguínea correspondientes y se me informen los resultados de los mismos una vez se hayan concluido.';

// ---------- Pie: toma de muestra ----------

export const CAMPOS_TOMA_MUESTRA_118: readonly CampoSimple[] = [
  { clave: 'fecha', etiqueta: 'Fecha de toma de muestra', tipo: 'fecha' },
  { clave: 'hora', etiqueta: 'Hora de toma de muestra', tipo: 'hora' },
  { clave: 'persona', etiqueta: 'Nombre y apellido de la persona que toma la muestra' },
];

export interface Estado118 {
  readonly diagnosticoPrincipal: DiagnosticoCie10Valor;
  readonly diagnosticoTransfusion: DiagnosticoCie10Valor;
  readonly servicio: Valores;
  readonly antecedentes: Valores;
  readonly laboratorio: Valores;
  readonly componentes: EstadoTablaConColumnas;
  readonly otroComponente: Valores;
  readonly tomaMuestra: Valores;
}

export function estadoInicial118(): Estado118 {
  return {
    diagnosticoPrincipal: { texto: '', cie10: null },
    diagnosticoTransfusion: { texto: '', cie10: null },
    servicio: valoresVacios(CAMPOS_SERVICIO_118),
    antecedentes: valoresVacios(CAMPOS_ANTECEDENTES_118),
    laboratorio: valoresVacios(CAMPOS_LABORATORIO_118),
    componentes: estadoInicialTablaConColumnas(FILAS_COMPONENTES_118, COLUMNAS_COMPONENTES_118),
    otroComponente: valoresVacios(CAMPOS_OTRO_COMPONENTE_118),
    tomaMuestra: valoresVacios(CAMPOS_TOMA_MUESTRA_118),
  };
}

/** Componentes con número o letras cargados: "CGRE ×2" (sigla entre paréntesis si la hay; "Otro" usa su descripción). */
export function componentesPedidos(e: Estado118): string[] {
  return FILAS_COMPONENTES_118.flatMap((f) => {
    const n = cantidadComponente118(e.componentes[f.clave]?.numero);
    if (n === undefined) return [];
    const cantidad = String(n);
    const nombre = f.clave === 'otro' ? (e.otroComponente.otroComponente ?? '').trim() || f.etiqueta : (/\(([^)]+)\)$/.exec(f.etiqueta)?.[1] ?? f.etiqueta);
    return [`${nombre} ×${cantidad}`];
  });
}

/** `ServiceRequest.code.text` — `SelectorRecurso` de 122 lo muestra tal cual. */
export function textoSolicitud118(e: Estado118): string {
  const pedidos = componentesPedidos(e);
  return pedidos.length ? `Solicitud de sangre: ${pedidos.join(', ')}` : 'Solicitud de sangre';
}

/** Lo que impide guardar la solicitud. Solo se exige lo que el instructivo/papel obliga a marcar: la prioridad y una cantidad válida. */
export function erroresObligatorios118(e: Estado118): string[] {
  const errores: string[] = [];
  if (prioridadFhir118(e.servicio.prioridad) === undefined) errores.push('Elija la prioridad de la solicitud (Emergencia, Urgencia o Rutina): no puede quedar vacía.');
  for (const f of FILAS_COMPONENTES_118) {
    const numero = (e.componentes[f.clave]?.numero ?? '').trim();
    if (numero !== '' && cantidadComponente118(numero) === undefined) errores.push(`La cantidad de "${f.etiqueta}" debe ser un número entero mayor que cero.`);
  }
  return errores;
}

/** Fecha (AAAA-MM-DD) y hora (24 h) locales de generación del pedido: el bloque F del papel las trae, se toman al guardar. */
export function fechaHoraGeneracion118(ahora: Date): { fecha: string; hora: string } {
  const dos = (n: number): string => String(n).padStart(2, '0');
  return { fecha: `${ahora.getFullYear()}-${dos(ahora.getMonth() + 1)}-${dos(ahora.getDate())}`, hora: `${dos(ahora.getHours())}:${dos(ahora.getMinutes())}` };
}

export function paresDe118(e: Estado118, ahora: Date = new Date()): Par[] {
  const generacion = fechaHoraGeneracion118(ahora);
  const dx = (titulo: string, d: DiagnosticoCie10Valor): Par => ({ pregunta: titulo, respuesta: conCodigoCie(d) });
  return [
    dx('Diagnóstico principal', e.diagnosticoPrincipal),
    ...paresDeCampos(CAMPOS_SERVICIO_118.slice(0, 4), e.servicio),
    dx('Diagnóstico para transfusión', e.diagnosticoTransfusion),
    ...paresDeCampos(CAMPOS_SERVICIO_118.slice(4), e.servicio),
    ...paresDeCampos(CAMPOS_ANTECEDENTES_118, e.antecedentes),
    ...paresDeCampos(CAMPOS_LABORATORIO_118, e.laboratorio),
    ...FILAS_COMPONENTES_118.map((f) => ({
      pregunta: `Componente — ${f.etiqueta}`,
      respuesta: [f.clave === 'otro' ? (e.otroComponente.otroComponente ?? '').trim() : '', resumenFilaTablaConColumnas(e.componentes, f.clave, COLUMNAS_COMPONENTES_118)].filter(Boolean).join(' · '),
    })),
    { pregunta: 'Solicitud de componentes sanguíneos con pruebas de compatibilidad en proceso', respuesta: TEXTO_LEGAL_118 },
    { pregunta: 'Fecha de generación del pedido', respuesta: generacion.fecha },
    { pregunta: 'Hora de generación del pedido', respuesta: generacion.hora },
    ...paresDeCampos(CAMPOS_TOMA_MUESTRA_118, e.tomaMuestra),
  ].filter((p) => p.respuesta.trim() !== '');
}
